/**
 * Reads for the Message builder: the template catalogue, the shared layouts
 * with the default pointer, and the token catalogue. Each read keeps the last
 * good answer while it refreshes (stale state) and ignores a response that
 * arrives after a newer request started.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { MessageLayoutSummary, MessageTemplateSummary, MessagingDefaultLayout } from "@v0y4ge/api-contracts";
import { builderApi, classifyFailure, type CatalogToken, type Failure, type TokenCatalog, type TokenContext } from "./api";

export type ReadState<T> =
  | { phase: "loading"; data: null; failure: null; refreshing: false }
  | { phase: "error"; data: T | null; failure: Failure; refreshing: false }
  | { phase: "ready"; data: T; failure: null; refreshing: boolean };

/**
 * One read, keyed by `identity` (what is read). A reload of the SAME identity keeps the last good data on
 * screen marked refreshing; a different identity starts from loading, so one context's answer is never
 * shown for another. Leaving (unmount) or moving on invalidates the in-flight request.
 */
export function useRead<T>(load: () => Promise<T>, identity: string): ReadState<T> & { reload: () => void } {
  const [held, setHeld] = useState<{ identity: string; state: ReadState<T> }>({ identity, state: { phase: "loading", data: null, failure: null, refreshing: false } });
  const seq = useRef(0);
  const loadRef = useRef(load); loadRef.current = load;
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    const mine = ++seq.current;
    setHeld((h) => {
      const s = h.state;
      if (h.identity === identity && s.data != null) return { identity, state: { phase: "ready", data: s.data, failure: null, refreshing: true } };
      return { identity, state: { phase: "loading", data: null, failure: null, refreshing: false } };
    });
    loadRef.current().then(
      (data) => { if (seq.current === mine) setHeld({ identity, state: { phase: "ready", data, failure: null, refreshing: false } }); },
      (error: unknown) => {
        if (seq.current !== mine) return;
        setHeld((h) => ({ identity, state: { phase: "error", data: h.identity === identity ? h.state.data : null, failure: classifyFailure(error), refreshing: false } }));
      },
    );
    return () => { seq.current++; };
  }, [identity, nonce]);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const state: ReadState<T> = held.identity === identity ? held.state : { phase: "loading", data: null, failure: null, refreshing: false };
  return Object.assign({}, state, { reload });
}

/* ---------- template catalogue (every page) ---------- */

async function loadAllTemplates(): Promise<MessageTemplateSummary[]> {
  const out: MessageTemplateSummary[] = [];
  for (let page = 1; page <= 50; page++) {
    const res = await builderApi.listMessageTemplates({ page, limit: 100 });
    out.push(...res.data);
    if (!res.pagination.hasMore || res.data.length === 0) break;
  }
  return out;
}

export function useTemplateCatalogue() {
  return useRead(loadAllTemplates, "catalogue");
}

/* ---------- layouts + default ---------- */

export interface LayoutLibrary { layouts: MessageLayoutSummary[]; defaultLayout: MessagingDefaultLayout }

export function useLayoutLibrary() {
  return useRead<LayoutLibrary>(async () => {
    const res = await builderApi.listMessageLayouts({ archived: "include" });
    return { layouts: res.data, defaultLayout: res.defaultLayout };
  }, "layouts");
}

/* ---------- token catalogue ---------- */

/** The row's own token context when the server sends one; else the group's. Unknown groups get `unknown`. */
export function tokenContextFor(groupId: string | null | undefined, rowContext?: TokenContext | null): TokenContext {
  if (rowContext) return rowContext;
  return groupId === "appointments" ? "appointment" : groupId === "events" ? "event" : groupId === "queues" ? "queue" : "unknown";
}

const tokenCache = new Map<TokenContext, Promise<TokenCatalog>>();
function loadTokens(context: TokenContext): Promise<TokenCatalog> {
  const cached = tokenCache.get(context);
  if (cached) return cached;
  const p: Promise<TokenCatalog> = builderApi.getMessageTokenCatalog(context).then((r) => r.data);
  p.catch(() => tokenCache.delete(context));
  tokenCache.set(context, p);
  return p;
}

export interface Tokens { tokens: CatalogToken[]; unavailable: CatalogToken[]; samples: Record<string, string> }

export function useTokens(context: TokenContext) {
  return useRead<Tokens>(async () => {
    const cat = await loadTokens(context);
    const samples: Record<string, string> = {};
    for (const tk of cat.tokens) samples[tk.id] = tk.sample;
    return { tokens: cat.tokens, unavailable: cat.unavailable, samples };
  }, `tokens:${context}`);
}
